// ============================================
// Audio Service - Combines TTS Client + Cache
// ============================================

import { ttsClient } from './TTSClient';
import { audioCache } from './AudioCache';
import { padForTTS } from './ttsUtils';
import type { TTSOptions, Word } from '@/types';
import { AUDIO_CONFIG } from '@/constants';

export { padForTTS };

// ============================================
// Audio Service
// ============================================

export class AudioService {
  private currentAudio: HTMLAudioElement | null = null;
  private currentUrl: string | null = null;

  // ==========================================
  // Core Playback
  // ==========================================

  async playAudio(text: string, options: TTSOptions = {}): Promise<void> {
    const { buttonElement, onStart, onEnd, onError } = options;

    // Stop any currently playing audio
    this.stopCurrentAudio();

    // Update button state
    if (buttonElement) {
      buttonElement.classList.add('loading');
    }

    onStart?.();

    const paddedText = padForTTS(text);

    try {
      // Try to get from cache first
      let audioBlob = await audioCache.get(paddedText);

      if (!audioBlob) {
        // Fetch from TTS service
        audioBlob = await ttsClient.getAudio(paddedText);
        // Cache for future use
        await audioCache.set(paddedText, audioBlob);
      }

      // Create audio element
      this.currentUrl = URL.createObjectURL(audioBlob);
      this.currentAudio = new Audio(this.currentUrl);

      if (buttonElement) {
        buttonElement.classList.remove('loading');
        buttonElement.classList.add('playing');
      }

      this.currentAudio.onended = () => {
        this.cleanup();
        if (buttonElement) {
          buttonElement.classList.remove('playing');
        }
        onEnd?.();
      };

      this.currentAudio.onerror = () => {
        this.cleanup();
        if (buttonElement) {
          buttonElement.classList.remove('loading', 'playing');
        }
        const error = new Error('Audio playback failed');
        onError?.(error);
      };

      await this.currentAudio.play();
    } catch (error) {
      this.cleanup();
      if (buttonElement) {
        buttonElement.classList.remove('loading', 'playing');
      }
      onError?.(error as Error);
      throw error;
    }
  }

  stopCurrentAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.currentUrl) {
      URL.revokeObjectURL(this.currentUrl);
      this.currentUrl = null;
    }
    this.currentAudio = null;
  }

  // ==========================================
  // Preloading
  // ==========================================

  // Warm the cache for the first N cards of a freshly-loaded deck so the
  // first audio tap doesn't block on the full TTS round trip. Fire-and-forget.
  preloadForDeck(deck: Word[]): void {
    const toPreload = deck.slice(0, AUDIO_CONFIG.preloadCount);
    toPreload.forEach(word => {
      this.preloadSingle(word.japanese);
    });
  }

  // Warm the next N cards ahead of the current position. Call site (deferred to
  // a component/state layer, since services don't observe navigation):
  //   audioService.preloadNext(currentIndex, deckWords)
  preloadNext(currentIndex: number, deck: Word[]): void {
    const start = currentIndex + 1;
    const end = Math.min(start + AUDIO_CONFIG.preloadCount, deck.length);
    const toPreload = deck.slice(start, end);

    toPreload.forEach(word => {
      // Preload in background without blocking
      this.preloadSingle(word.japanese);
    });
  }

  // Texts with a preload currently in flight. Guards against rapid
  // navigation double-fetching the same not-yet-cached clip (each TTS
  // synthesis is billed) — the IDB cache only dedupes AFTER a fetch lands.
  private preloadsInFlight = new Set<string>();

  // Background-preload budget: timestamps of preload fetches STARTED in the
  // trailing window. The TTS proxy allows 30 requests/min per IP shared by
  // playback and preloading — an empty cache plus fast navigation could fill
  // that window with background warming and get the user's actual play
  // request rejected. Preloading yields once it has used half the window;
  // user-initiated playAudio is never budgeted.
  private static readonly PRELOAD_WINDOW_MS = 60 * 1000;
  private static readonly PRELOAD_BUDGET_PER_WINDOW = 15;
  private preloadStarts: number[] = [];

  private preloadBudgetAvailable(): boolean {
    const cutoff = Date.now() - AudioService.PRELOAD_WINDOW_MS;
    this.preloadStarts = this.preloadStarts.filter(t => t > cutoff);
    return this.preloadStarts.length < AudioService.PRELOAD_BUDGET_PER_WINDOW;
  }

  // Fetch-and-cache a single clip without playing it. Never throws: no-ops if
  // already cached, already being preloaded, offline, or the TTS circuit is
  // open, and swallows any fetch/cache error so a failed preload can never
  // surface to the user.
  private async preloadSingle(text: string): Promise<void> {
    // Skip when the browser reports offline — the fetch would just fail.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const paddedText = padForTTS(text);
    if (this.preloadsInFlight.has(paddedText)) return;
    this.preloadsInFlight.add(paddedText);
    try {
      const isCached = await audioCache.has(paddedText);
      if (!isCached && ttsClient.isAvailable() && this.preloadBudgetAvailable()) {
        this.preloadStarts.push(Date.now());
        const blob = await ttsClient.getAudio(paddedText);
        await audioCache.set(paddedText, blob);
      }
    } catch {
      // Silent fail for preloading
    } finally {
      this.preloadsInFlight.delete(paddedText);
    }
  }

  // ==========================================
  // Status
  // ==========================================

  isPlaying(): boolean {
    return this.currentAudio !== null && !this.currentAudio.paused;
  }

  isAvailable(): boolean {
    return ttsClient.isAvailable();
  }

  async getCacheStats() {
    return audioCache.getStats();
  }

  async clearCache(): Promise<void> {
    await audioCache.clear();
  }
}

// ============================================
// Singleton Export
// ============================================

export const audioService = new AudioService();
